<script setup lang="ts">
/**
 * Footer global: identitas penyelenggara, kanal kontak, dan halaman kebijakan.
 *
 * Dipisah dari layout supaya isinya (yang bersifat wajib untuk situs yang
 * menerima pembayaran) ikut terbawa saat tampilan diganti desain baru —
 * komponen ini tinggal dipanggil ulang, atau datanya diambil dari
 * useIdentitas() yang sama.
 *
 * Semua baris yang bergantung pada data pemilik dibungkus v-if: sebelum env
 * diisi, footer tetap tampil rapi tanpa baris kosong atau tautan mati.
 */
const identitas = useIdentitas()

const tahunSekarang = new Date().getFullYear()

/** "2026" bila baru berdiri tahun ini, "2026–2028" bila sudah lewat. */
const rentangTahun = computed(() => {
  if (!identitas.adaTahun) return String(tahunSekarang)
  const mulai = Number(identitas.tahunBerdiri)
  if (!Number.isFinite(mulai) || mulai >= tahunSekarang) return String(tahunSekarang)
  return `${mulai}–${tahunSekarang}`
})
</script>

<template>
  <footer class="footer">
    <div class="container footer-inner">
      <div class="footer-kolom">
        <strong>Alat gratis</strong>
        <NuxtLink to="/legacy/kompres-foto">Kompres foto ke ukuran KB</NuxtLink>
        <NuxtLink to="/legacy/kompres-pdf">Kompres PDF ke ukuran KB</NuxtLink>
      </div>
      <div class="footer-kolom">
        <strong>Produk</strong>
        <NuxtLink to="/legacy/berkaskilat">BerkasKilat</NuxtLink>
        <NuxtLink to="/legacy/rapikontak">RapiKontak</NuxtLink>
        <NuxtLink to="/legacy/harga">Harga</NuxtLink>
      </div>
      <div class="footer-kolom">
        <strong>Akses</strong>
        <NuxtLink to="/legacy/redeem">Tukar kode aktivasi</NuxtLink>
        <NuxtLink to="/legacy/app">Aplikasi RapiKontak</NuxtLink>
        <NuxtLink to="/legacy/app/berkas">Aplikasi BerkasKilat</NuxtLink>
      </div>
      <div class="footer-kolom">
        <strong>Ketentuan</strong>
        <NuxtLink to="/legacy/privasi">Kebijakan privasi</NuxtLink>
        <NuxtLink to="/legacy/syarat">Syarat layanan</NuxtLink>
        <NuxtLink to="/legacy/refund">Pengembalian dana</NuxtLink>
      </div>
      <div v-if="identitas.adaKontak" class="footer-kolom">
        <strong>Bantuan</strong>
        <a
          v-if="identitas.adaWa"
          :href="identitas.tautanWa({ konteks: 'Footer' })"
          target="_blank"
          rel="noopener"
        >
          WhatsApp {{ identitas.waTampil }}
        </a>
        <a v-if="identitas.adaEmail" :href="identitas.tautanEmail()">{{ identitas.email }}</a>
      </div>
    </div>

    <div class="container footer-catatan">
      <p v-if="identitas.adaNama" class="footer-badan">
        {{ identitas.namaBadanUsaha }}
        <span v-if="identitas.adaAlamat"> — {{ identitas.alamat }}</span>
      </p>
      <p v-if="identitas.pseTerdaftar" class="footer-pse">
        Terdaftar sebagai Penyelenggara Sistem Elektronik (PSE) Kementerian Komunikasi dan Digital.
      </p>
      <p>
        Berkas yang kamu unggah, beserta hasil olahannya, dihapus otomatis paling lama
        {{ RETENSI_BERKAS_JAM }} jam setelah diproses. Selengkapnya di
        <NuxtLink to="/legacy/privasi">kebijakan privasi</NuxtLink>.
      </p>
      <p>
        Batas ukuran berkas ditetapkan oleh masing-masing instansi dan bisa berubah sewaktu-waktu.
        Selalu periksa kembali pengumuman resmi instansi tujuan sebelum mengunggah.
      </p>
      <p class="footer-hak">
        &copy; {{ rentangTahun }}
        <template v-if="identitas.adaNama">{{ identitas.namaBadanUsaha }} — </template>
        tools.coreasia.id
      </p>
    </div>
  </footer>
</template>

<style scoped>
.footer-catatan p {
  margin: 0 0 0.5rem;
}

.footer-catatan p:last-child {
  margin-bottom: 0;
}

.footer-catatan a {
  color: var(--accent);
}

.footer-badan {
  color: var(--text);
  font-weight: 600;
}

.footer-hak {
  padding-top: 0.35rem;
}
</style>
